// ApplicationPage.jsx (Updated with Tab System)
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import logger from '../utils/logger';
import ApplicationModal from '../components/Application/ApplicationModal';
import ApplicationSections from '../components/Application/ApplicationSections';
import MyApplicationsCard from '../components/Application/MyApplicationsCard';
import CreatePositionButton from '../components/Application/CreatePositionButton';
import CreatePositionModal from '../components/Application/CreatePositionModal';
import SelfTrackingApplicationModal from '../components/Application/SelfTrackingApplicationModal';
import SavedPage from '../pages/SavedPage'; // Import your existing SavedPage
import styles from '../components/Application/Application.module.css';

// Tab component
function ApplicationTabs({ currentTab, onTabChange }) {
  const tabs = [
    { id: 'applications', label: 'Applications' },
    { id: 'saved', label: 'Saved' }
  ];

  return (
    <div className={styles.applicationTabsCard}>
      <div className={styles.tabsContainer}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`${styles.tabButton} ${currentTab === tab.id ? styles.active : styles.inactive}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ApplicationPage() {
  const { positionId } = useParams();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState('applications');
  const [myApplications, setMyApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [existingApplication, setExistingApplication] = useState(null);
  const [showCreatePositionModal, setShowCreatePositionModal] = useState(false);
  const [showSelfTrackingModal, setShowSelfTrackingModal] = useState(false);
  const [editingSelfTracking, setEditingSelfTracking] = useState(null);

  const userData = JSON.parse(localStorage.getItem('user')) || {};
  const token = localStorage.getItem('token');
  const isPositionSpecific = !!positionId;

  // Separate native and self-tracking applications
  const nativeApplications = myApplications.filter(app => app.isNative === true);
  const selfTrackingApplications = myApplications.filter(app => app.isNative === false);


  const handleTabChange = (tab) => {
    setCurrentTab(tab);
  };

  const handlePositionCreated = () => {
    setShowCreatePositionModal(false);
    fetchMyApplications();
  };

  useEffect(() => {
    if (isPositionSpecific) {
      handlePositionApplication();
    } else {
      fetchMyApplications();
    }
  }, [positionId]);

  const handlePositionApplication = async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        const positionResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/positions/${positionId}`, config);
        logger.log('Position data loaded successfully');
        setSelectedPosition(positionResponse.data);

        try {
          const appResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/applications/my-application/${positionId}`,
            config
          );
          logger.log('Existing application found');
          setExistingApplication(appResponse.data);
        } catch (appError) {
          if (appError.response && appError.response.status === 404) {
            setShowApplicationModal(true);
          } else {
            console.error('Error checking application:', appError);
            alert('There was a problem checking your application status. Please try again.');
            navigate('/student-dashboard');
          }
        }
      } catch (posError) {
        console.error('Error fetching position:', posError);
        alert('Position not found or has been removed.');
        navigate('/student-dashboard');
      }
    } catch (error) {
      console.error('Overall error in handlePositionApplication:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyApplications = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Fetch native applications
      const nativeResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/applications/my-applications`, config);
      logger.log('Native applications loaded');

      // Fetch self-tracking applications
      const selfTrackingResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/self-tracking-applications`, config);
      logger.log('Self-tracking applications loaded');

      // Combine and mark them
      const nativeApps = (nativeResponse.data || []).map(app => ({ ...app, isNative: true }));
      const selfTrackingApps = (selfTrackingResponse.data || []).map(app => ({ ...app, isNative: false }));

      setMyApplications([...nativeApps, ...selfTrackingApps]);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setMyApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawApplication = async (applicationId, isNative = true) => {
    if (!token || !window.confirm('Are you sure you want to withdraw this application?')) return;

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (isNative) {
        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/applications/${applicationId}`, config);
      } else {
        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/self-tracking-applications/${applicationId}`, config);
      }

      if (isPositionSpecific && existingApplication?._id === applicationId) {
        setExistingApplication(null);
        handlePositionApplication();
      } else {
        fetchMyApplications();
      }

      alert('Application withdrawn successfully!');
    } catch (error) {
      console.error('Error withdrawing application:', error);
      alert('Failed to withdraw application');
    }
  };

  const handleApplicationSubmit = async (applicationData) => {
    if (!token) return;

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const dataToSubmit = {
        ...applicationData,
        positionId: positionId || applicationData.positionId,
        isNative: true
      };

      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/applications`, dataToSubmit, config);
      setShowApplicationModal(false);
      setSelectedPosition(null);

      if (isPositionSpecific) {
        alert('Application submitted successfully!');
        navigate('/applications');
      } else {
        fetchMyApplications();
        alert('Application submitted successfully!');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('Failed to submit application');
      }
    }
  };

  const handleCreateSelfTracking = async (applicationData) => {
    if (!token) return;

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      let result;

      if (editingSelfTracking) {
        result = await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/self-tracking-applications/${editingSelfTracking._id}`,
          applicationData,
          config
        );
        alert('Self-tracking application updated successfully!');
      } else {
        result = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/self-tracking-applications`,
          applicationData,
          config
        );
        alert('Self-tracking application created successfully!');
      }

      setShowSelfTrackingModal(false);
      setEditingSelfTracking(null);
      // Force refresh
      await fetchMyApplications();
    } catch (error) {
      console.error('Error with self-tracking application:', error);
      console.error('Error response:', error.response?.data);
      alert(`Failed to save self-tracking application: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleUpdateSelfTracking = async (applicationId, updateData) => {
    if (!token) return;

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/self-tracking-applications/${applicationId}`, updateData, config);
      fetchMyApplications();
      alert('Application updated successfully!');
    } catch (error) {
      console.error('Error updating self-tracking application:', error);
      alert('Failed to update application');
    }
  };

  const handleEditSelfTracking = (application) => {
    setEditingSelfTracking(application);
    setShowSelfTrackingModal(true);
  };

  const getApplicationStatusStats = () => {
    const stats = {
      total: myApplications.length,
      pending: myApplications.filter(app => app.status === 'pending').length,
      accepted: myApplications.filter(app => app.status === 'accepted').length,
      rejected: myApplications.filter(app => app.status === 'rejected').length
    };
    return stats;
  };

  // If this is a position-specific page and we have an existing application
  if (isPositionSpecific && existingApplication) {
    return (
      <div className={styles.applicationPage}>
        <div className={styles.applicationContainer}>
          <div className={styles.applicationHeader}>
            <h1 className={styles.applicationTitle}>Your Application</h1>
            <p className={styles.applicationSubtitle}>
              You have already applied to this position
            </p>
          </div>

          <div className={styles.applicationContent}>
            <div className={styles.applicationsGrid}>
              <MyApplicationsCard
                application={existingApplication}
                onWithdraw={(id) => handleWithdrawApplication(id, true)}
              />
            </div>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button
                className={styles.browsePositionsBtn}
                onClick={() => navigate('/applications')}
              >
                View All Applications
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular applications page with tabs
  const stats = getApplicationStatusStats();

  return (
    <div className={styles.applicationPage}>
      <div className={styles.applicationContainer}>
        {/* Header - Always visible */}
        <div className={styles.applicationHeader}>
          <h1 className={styles.applicationTitle}>
            {isPositionSpecific ? 'Apply to Position' : 'My Applications'}
          </h1>
          <p className={styles.applicationSubtitle}>
            {isPositionSpecific
              ? 'Complete your application for this research position'
              : 'Track your application status and manage your submissions'
            }
          </p>
        </div>

        {/* Stats Summary - Always visible on general applications page */}
        {!isPositionSpecific && stats.total > 0 && (
          <div className={styles.statsContainer}>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{stats.total}</div>
              <div className={styles.statLabel}>Total Applications</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber} style={{ color: '#f59e0b' }}>{stats.pending}</div>
              <div className={styles.statLabel}>Pending</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber} style={{ color: '#22c55e' }}>{stats.accepted}</div>
              <div className={styles.statLabel}>Accepted</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber} style={{ color: '#ef4444' }}>{stats.rejected}</div>
              <div className={styles.statLabel}>Rejected</div>
            </div>
          </div>
        )}

        {/* Tabs - only show on general applications page, positioned after stats */}
        {!isPositionSpecific && (
          <ApplicationTabs currentTab={currentTab} onTabChange={handleTabChange} />
        )}

        {/* Content */}
        <div className={styles.applicationContent}>
          {loading ? (
            <div className={styles.applicationLoading}>
              <div className={styles.loadingSpinner}></div>
              <p>
                {isPositionSpecific
                  ? 'Loading application form...'
                  : 'Loading your applications...'
                }
              </p>
            </div>
          ) : (
            !isPositionSpecific && (
              <>
                {currentTab === 'applications' && (
                  <ApplicationSections
                    nativeApplications={nativeApplications}
                    selfTrackingApplications={selfTrackingApplications}
                    onWithdraw={handleWithdrawApplication}
                    onUpdateSelfTracking={handleUpdateSelfTracking}
                    onEditSelfTracking={handleEditSelfTracking}
                    onAddSelfTracking={() => setShowSelfTrackingModal(true)}
                  />
                )}
                {currentTab === 'saved' && <SavedPage />}
              </>
            )
          )}
        </div>
      </div>

      {/* Application Modal */}
      {showApplicationModal && selectedPosition && (
        <ApplicationModal
          position={selectedPosition}
          onSubmit={handleApplicationSubmit}
          onClose={() => {
            setShowApplicationModal(false);
            setSelectedPosition(null);
            if (isPositionSpecific) {
              navigate('/student-dashboard');
            }
          }}
        />
      )}

      {/* Self-Tracking Application Modal */}
      {showSelfTrackingModal && (
        <SelfTrackingApplicationModal
          application={editingSelfTracking}
          onSubmit={handleCreateSelfTracking}
          onClose={() => {
            setShowSelfTrackingModal(false);
            setEditingSelfTracking(null);
          }}
        />
      )}

      {/* Create Position Modal - Keep this for professor functionality if needed */}
      {showCreatePositionModal && (
        <CreatePositionModal
          userData={{ ...userData, token }}
          onPositionCreated={handlePositionCreated}
          onClose={() => setShowCreatePositionModal(false)}
        />
      )}
    </div>
  );
}